/**
 * Central module registry — single source of truth for the mobile
 * navigation bar AND the More-menu starring affordance.
 *
 * v77.99.108e (iter 122.icon-audit) — every module's icon is sourced
 * from `/app/frontend/src/lib/moduleIcons.js` so the same Lucide icon
 * is used by the bottom nav, the More-menu cards, the AppShell side
 * nav, every page header AND the Dashboard "This week" widgets. No
 * screen may substitute a different icon for an existing module.
 */
import { MODULE_ICONS } from "@/lib/moduleIcons";
import {
  FileDown, TrendingUp, ArrowLeftRight, CalendarClock,
  ScrollText, Bug, Inbox,
} from "lucide-react";

const {
  expenses:       ExpensesIcon,
  maintenance:    MaintenanceIcon,
  messages:       MessagesIcon,
  calendar:       CalendarIcon,
  contact_visits: ContactVisitsIcon,
  documents:      DocumentsIcon,
  requests:       RequestsIcon,
  journal:        JournalIcon,
  children:       ChildrenIcon,
  settings:       SettingsIcon,
} = MODULE_ICONS;

/**
 * @typedef {Object} NavModule
 * @property {string} slug      Last URL segment, used as the persisted identifier.
 * @property {string} label     User-visible label.
 * @property {string} to        Full route path (used when rendering NavLinks).
 * @property {any}    icon      Lucide icon component.
 * @property {string} [permKey] Permission key (matches AppShell visibility).
 * @property {boolean}[adminOnly] Hide from non-admin users.
 * @property {boolean}[mediatorOnly] Show only when the viewer is in the
 *                              "third-party" mediator portal.
 */

/**
 * Modules eligible for the mobile bottom-nav middle slots and for the
 * ⭐ favourites affordance in the More menu. The order here is the
 * order they appear in the More menu's "All modules" list.
 *
 * Dashboard, More and Appearance are intentionally absent:
 *   • Dashboard is the fixed first slot.
 *   • More is the fixed last slot.
 *   • Appearance lives inside Settings → Appearance and is not a
 *     navigable module of its own.
 */
export const MOBILE_NAV_MODULES = [
  { slug: "messages",            label: "Messages",          to: "/app/messages",          icon: MessagesIcon,       permKey: "view_messages" },
  { slug: "calendar",            label: "Calendar",          to: "/app/calendar",          icon: CalendarIcon,       permKey: "view_calendar" },
  { slug: "contact-visits",      label: "Contact Visits",    to: "/app/contact-visits",    icon: ContactVisitsIcon },
  { slug: "requests",            label: "Requests",          to: "/app/requests",          icon: RequestsIcon,       permKey: "view_requests" },
  { slug: "expenses",            label: "Expenses",          to: "/app/expenses",          icon: ExpensesIcon,       permKey: "view_expenses" },
  { slug: "upcoming-expenses",   label: "Upcoming Expenses", to: "/app/upcoming-expenses", icon: CalendarClock,      permKey: "view_expenses" },
  { slug: "maintenance",         label: "Maintenance",       to: "/app/maintenance",       icon: MaintenanceIcon,    permKey: "view_maintenance" },
  { slug: "journal",             label: "Journal",           to: "/app/journal",           icon: JournalIcon,        permKey: "view_journal" },
  { slug: "documents",           label: "Documents",         to: "/app/documents",         icon: DocumentsIcon,      permKey: "view_documents" },
  { slug: "children",            label: "Children",          to: "/app/children",          icon: ChildrenIcon,       permKey: "view_children" },
  { slug: "court-bundle",        label: "Court Bundle",      to: "/app/court-bundle",      icon: FileDown,           permKey: "view_court_bundle" },
  // v77.99.140 (iter 124.exports-inbox) — Central "Document Centre"
  // seed: unified list of every async export (Expenses PDFs, Court
  // Bundles, and future Contact Visit / AI reports) so users can
  // return days later to re-download. Sits in the More menu — no
  // bottom-nav slot needed for this yet.
  { slug: "exports",             label: "Exports",           to: "/app/exports",           icon: Inbox },
  { slug: "insights",            label: "Insights",          to: "/app/insights",          icon: TrendingUp },
  { slug: "third-party-access",  label: "Third-party Access",to: "/app/third-party-access",icon: ArrowLeftRight },
  { slug: "activity",            label: "Activity Log",      to: "/app/activity",          icon: ScrollText },
  { slug: "settings",            label: "Settings",          to: "/app/settings",          icon: SettingsIcon },
];

// Re-export the bug icon so consumers don't need a separate lucide
// import for the "Report a bug" affordance.
export { Bug };

/** Return the module entry for a given slug (or undefined). */
export function moduleBySlug(slug) {
  return MOBILE_NAV_MODULES.find((m) => m.slug === slug);
}

/** Visibility check that mirrors AppShell's gating logic. */
export function moduleVisibleTo(module, user) {
  if (!module) return false;
  if (module.adminOnly && user?.role !== "admin") return false;
  if (!module.permKey) return true;
  const perms = user?.permissions;
  if (!perms) return true;
  return !!perms[module.permKey];
}

/** Role-based defaults when the user has not customised their nav. */
export function defaultMobileNavSlots(user) {
  if (user?.role === "mediator") return ["calendar", "contact-visits", "documents"];
  return ["messages", "calendar", "contact-visits"];
}

/**
 * Resolve the user's effective 3-slot bottom-nav choice given the
 * stored preference, viewer permissions, and role-based fallback.
 *
 * Returns an array of up to 3 module entries (never more), with any
 * slugs the viewer can't see filtered out, and remaining gaps filled
 * from the default list so the bar always has 3 middle items.
 */
export function resolveMobileNavSlots(user) {
  const stored = (user?.mobile_nav || []).filter(Boolean);
  const defaults = defaultMobileNavSlots(user);
  const out = [];
  const used = new Set();
  for (const slug of [...stored, ...defaults]) {
    if (used.has(slug)) continue;
    const m = moduleBySlug(slug);
    if (!m || !moduleVisibleTo(m, user)) continue;
    out.push(m);
    used.add(slug);
    if (out.length >= 3) break;
  }
  return out;
}
